import { Link } from "react-router-dom";
import type { PriceRow } from "@/mocks/priceTables";

/** 施術のお悩みタグ（Greige / Ivory系・Charcoal文字・控えめな角丸） */
export function ConcernTags({ tags, className = "" }: { tags?: string[]; className?: string }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-sm border border-foreground-950/12 bg-background-100 px-2 py-0.5 text-[10px] leading-[1.6] text-foreground-700"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

type PriceRowTableProps = {
  rows: PriceRow[];
  /** light: 明るい背景 × 濃色文字（既定） / dark: チャコール背景 × ウォームホワイト文字 */
  tone?: "light" | "dark";
};

/**
 * 施術名｜内容｜料金 の基本料金表。
 * PC: 表形式 / スマホ: 施術名 → 内容 → 料金 の縦型レイアウト
 *
 * tone="dark" を指定すると、医療脱毛ページの男性料金と同じチャコール反転配色で描画する。
 */
export default function PriceRowTable({ rows, tone = "light" }: PriceRowTableProps) {
  const isDark = tone === "dark";

  return (
    <>
      {/* PC: 表形式 */}
      <div className="mt-6 hidden md:block">
        <div
          className={`grid grid-cols-[1.3fr_1.7fr_120px] border-t pb-3 pt-4 text-[11px] tracking-[0.15em] ${
            isDark ? "border-background-50/25 text-background-300" : "border-foreground-950/20 text-foreground-500"
          }`}
        >
          <span>施術名</span>
          <span>内容</span>
          <span className="text-right">料金</span>
        </div>
        {rows.map((row) => (
          <div
            key={row.name}
            className={`grid grid-cols-[1.3fr_1.7fr_120px] items-baseline border-t py-5 ${
              isDark ? "border-background-50/15" : "border-foreground-950/12"
            }`}
          >
            <div className="pr-6">
              <span className={`font-heading text-[15px] ${isDark ? "text-background-50" : "text-foreground-950"}`}>
                {row.href ? (
                  <Link
                    to={row.href}
                    className={`group/row inline-flex items-center gap-1.5 underline-offset-4 transition-colors hover:underline ${
                      isDark ? "hover:text-accent-400" : "hover:text-secondary-700"
                    }`}
                  >
                    {row.name}
                    <i
                      className={`ri-arrow-right-line text-sm transition-transform group-hover/row:translate-x-0.5 ${
                        isDark ? "text-accent-400" : "text-secondary-500"
                      }`}
                      aria-hidden
                    />
                  </Link>
                ) : (
                  row.name
                )}
              </span>
              <ConcernTags tags={row.tags} className="mt-2" />
            </div>
            <span
              className={`pr-6 text-[12.5px] leading-[1.9] ${isDark ? "text-background-200" : "text-foreground-600"}`}
            >
              {row.note}
            </span>
            <span
              className={`text-right font-label text-[15px] ${isDark ? "text-background-100" : "text-foreground-900"}`}
            >
              {row.price}
            </span>
          </div>
        ))}
      </div>

      {/* スマホ: 施術名 → 内容 → 料金 の縦型レイアウト */}
      <div className="mt-6 space-y-3 md:hidden">
        {rows.map((row) => (
          <div
            key={`${row.name}-sp`}
            className={`border p-5 ${isDark ? "border-background-50/15 bg-foreground-900" : "border-foreground-950/12 bg-background-50"}`}
          >
            <span
              className={`en-eyebrow block text-[9px] ${isDark ? "text-accent-400" : "text-secondary-500"}`}
            >
              施術名
            </span>
            {row.href ? (
              <Link
                to={row.href}
                className={`mt-2 inline-flex items-center gap-1.5 font-heading text-[16px] underline-offset-4 hover:underline ${
                  isDark ? "text-background-50" : "text-foreground-950"
                }`}
              >
                {row.name}
                <i
                  className={`ri-arrow-right-line text-sm ${isDark ? "text-accent-400" : "text-secondary-500"}`}
                  aria-hidden
                />
              </Link>
            ) : (
              <span
                className={`mt-2 block font-heading text-[16px] ${isDark ? "text-background-50" : "text-foreground-950"}`}
              >
                {row.name}
              </span>
            )}
            <ConcernTags tags={row.tags} className="mt-2.5" />

            <span
              className={`en-eyebrow mt-5 block text-[9px] ${isDark ? "text-accent-400" : "text-secondary-500"}`}
            >
              内容
            </span>
            <span
              className={`mt-2 block text-[12.5px] leading-[1.9] ${isDark ? "text-background-200" : "text-foreground-600"}`}
            >
              {row.note}
            </span>

            <div
              className={`mt-5 flex items-center justify-between border-t pt-3 ${
                isDark ? "border-background-50/15" : "border-foreground-950/10"
              }`}
            >
              <span className={`en-eyebrow text-[9px] ${isDark ? "text-accent-400" : "text-secondary-500"}`}>
                料金
              </span>
              <span
                className={`font-label text-[16px] ${isDark ? "text-background-100" : "text-foreground-900"}`}
              >
                {row.price}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}